/**
 * The shared interface vocabulary.
 *
 * Every control in Lattice is built from these. Keeping them in one file means
 * the product has one button, one avatar and one tooltip rather than six
 * slightly different ones — which is most of what makes an application feel
 * considered rather than assembled.
 *
 * They are deliberately thin: no theme provider, no variant explosion, no
 * animation library. Colour comes from the tokens in index.css, so light and
 * dark are handled once, globally, and never thought about again.
 */
import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import type { User } from '@lattice/core'

/* ------------------------------ Button ------------------------------ */

const buttonStyles = cva(
  'snap inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium ' +
  'select-none disabled:pointer-events-none disabled:opacity-40 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-1',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--fg)] text-[var(--bg)] hover:opacity-90',
        accent: 'bg-[var(--accent)] text-[var(--fg-onAccent)] hover:opacity-90',
        secondary: 'bg-[var(--bg-raised)] text-[var(--fg)] border border-[var(--border)] hover:bg-[var(--bg-hover)]',
        ghost: 'text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]',
        danger: 'bg-[var(--bg-raised)] border border-[var(--border)] text-[var(--deleted)] hover:bg-[var(--bg-hover)]',
      },
      size: {
        sm: 'h-6.5 px-2 text-xs rounded-[var(--radius-sm)]',
        md: 'h-8 px-3 text-sm rounded-[var(--radius-md)]',
        lg: 'h-9.5 px-4 text-base rounded-[var(--radius-md)]',
        icon: 'h-7 w-7 rounded-[var(--radius-sm)]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'
    return <Component ref={ref} className={cn(buttonStyles({ variant, size }), className)} {...props} />
  },
)
Button.displayName = 'Button'

/* ------------------------------ Tooltip ------------------------------ */

export const TooltipProvider = ({ children }: { children: React.ReactNode }) => (
  // 400ms: long enough not to flicker while the pointer crosses a toolbar,
  // short enough that deliberately hovering feels answered.
  <TooltipPrimitive.Provider delayDuration={400} skipDelayDuration={300}>{children}</TooltipPrimitive.Provider>
)

export function Tooltip({ label, keys, children, side = 'bottom' }: {
  label: React.ReactNode
  keys?: string[]
  children: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
}) {
  if (!label) return <>{children}</>
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 pop-in rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] px-2 py-1 text-xs text-[var(--fg)] shadow-pop flex items-center gap-1.5"
        >
          <span>{label}</span>
          {keys?.length ? <Kbd keys={keys} /> : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

/* -------------------------------- Kbd -------------------------------- */

/** Render a shortcut. Uses the platform's own glyphs so it reads natively. */
export function Kbd({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {keys.map((k, i) => (
        <kbd
          key={i}
          className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-[3px] border border-[var(--border)] bg-[var(--bg-inset)] px-1 font-sans text-[10px] font-medium text-[var(--fg-subtle)]"
        >
          {k}
        </kbd>
      ))}
    </span>
  )
}

/** True on macOS, where the modifier is ⌘ rather than Ctrl. */
export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
export const MOD = IS_MAC ? '⌘' : 'Ctrl'

/* ------------------------------ Avatar ------------------------------ */

/**
 * Six fixed hues, one per person.
 *
 * Deliberately not derived from a hash of the name: a fixed assignment means
 * two colleagues can never collide, and everyone's colour is stable forever.
 * They are desaturated to sit quietly inside a monochrome interface.
 */
const ACCENTS = [
  'oklch(62% 0.13 256)', 'oklch(62% 0.13 320)', 'oklch(60% 0.13 25)',
  'oklch(58% 0.12 150)', 'oklch(64% 0.12 75)', 'oklch(60% 0.12 290)',
]

export function Avatar({ user, size = 20, className }: { user: User; size?: number; className?: string }) {
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white select-none', className)}
      style={{
        width: size, height: size,
        background: ACCENTS[user.accent % ACCENTS.length],
        fontSize: Math.max(8, Math.round(size * 0.4)),
        letterSpacing: '0.01em',
      }}
      title={user.name}
      aria-label={user.name}
    >
      {user.initials}
    </span>
  )
}

/** Overlapping avatars for a thread's participants. */
export function AvatarStack({ users, size = 18, max = 4 }: { users: User[]; size?: number; max?: number }) {
  const shown = users.slice(0, max)
  return (
    <span className="inline-flex items-center">
      {shown.map((u, i) => (
        <span key={u.id} style={{ marginLeft: i === 0 ? 0 : -size * 0.3, zIndex: shown.length - i }}
              className="rounded-full ring-2 ring-[var(--bg)]">
          <Avatar user={u} size={size} />
        </span>
      ))}
      {users.length > max && (
        <span className="ml-1 text-xs text-[var(--fg-subtle)] tabular">+{users.length - max}</span>
      )}
    </span>
  )
}

/* ------------------------------- Badge ------------------------------- */

export function Badge({ children, tone = 'neutral', className }: {
  children: React.ReactNode
  tone?: 'neutral' | 'added' | 'modified' | 'deleted' | 'renamed' | 'accent'
  className?: string
}) {
  const tones: Record<string, string> = {
    neutral: 'text-[var(--fg-muted)] border-[var(--border)]',
    added: 'text-[var(--added)] border-[color-mix(in_oklch,var(--added)_35%,transparent)]',
    modified: 'text-[var(--modified)] border-[color-mix(in_oklch,var(--modified)_35%,transparent)]',
    deleted: 'text-[var(--deleted)] border-[color-mix(in_oklch,var(--deleted)_35%,transparent)]',
    renamed: 'text-[var(--renamed)] border-[color-mix(in_oklch,var(--renamed)_35%,transparent)]',
    accent: 'text-[var(--accent)] border-[color-mix(in_oklch,var(--accent)_35%,transparent)]',
  }
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-1.5 h-[18px] text-[10px] font-medium uppercase tracking-wide',
      tones[tone], className,
    )}>
      {children}
    </span>
  )
}

/* ---------------------------- Empty state ---------------------------- */

export function EmptyState({ icon: Icon, title, hint, action }: {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>
  title: string
  hint?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex h-full min-h-40 flex-col items-center justify-center gap-2 px-8 text-center fade-in">
      {Icon && <Icon className="h-7 w-7 text-[var(--fg-subtle)]" strokeWidth={1.25} />}
      <div className="text-sm font-medium text-[var(--fg)]">{title}</div>
      {hint && <div className="max-w-sm text-sm text-[var(--fg-subtle)] leading-relaxed">{hint}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

/* ------------------------------ Spinner ------------------------------ */

export function Spinner({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <svg className={cn('animate-spin', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

/* ---------------------------- Separator ------------------------------ */

export const Separator = ({ className }: { className?: string }) => (
  <div className={cn('h-px bg-[var(--border)]', className)} />
)

/* ------------------------- Highlighted text -------------------------- */

/**
 * Show which characters a search matched.
 *
 * This is the small detail that makes a command palette feel like it
 * understands the query rather than merely filtering.
 */
export function Highlight({ text, spans }: { text: string; spans: readonly (readonly [number, number])[] }) {
  if (!spans.length) return <>{text}</>
  const parts: React.ReactNode[] = []
  let at = 0
  spans.forEach(([start, end], i) => {
    if (start > at) parts.push(<span key={`p${i}`}>{text.slice(at, start)}</span>)
    parts.push(<mark key={`m${i}`} className="bg-transparent text-[var(--accent)] font-semibold">{text.slice(start, end)}</mark>)
    at = end
  })
  if (at < text.length) parts.push(<span key="tail">{text.slice(at)}</span>)
  return <>{parts}</>
}
